import {
	useQuery,
	useQueryClient,
	UseQueryResult,
} from "@tanstack/react-query";
import { isNotNullOrUndefined } from "newui/utils/objectUtil";

/**
 * Returns stale time in MS
 * Short: 5 minutes
 * Medium: 15 minutes
 * Long: 30 minutes
 */
export enum StaleTime {
	Short = 5 * 60 * 1000, // 5 minutes
	Medium = 15 * 60 * 1000, // 15 minutes
	Long = 30 * 60 * 1000, // 30 minutes
}

/**
 * provides a response for the Common Lookup Data
 */
export interface CommonLookupResponse<T> {
	error: unknown;
	isError: boolean;
	isLoading: boolean;
	values: T[];
	clearCache: () => void;
}

/**
 * Provides a data fetch hook for common data types
 * T = the tyope to be returned
 * P = the optional parameter for the fetch function
 * @param key
 * @param fetcher
 * @returns
 */
export function useCommonLookup<T, P = undefined>(
	key: string,
	fetcher: (param?: P) => Promise<T[]>,
	param?: P,
	staleTimeOverrideMinutes?: number
): CommonLookupResponse<T> {
	const queryClient = useQueryClient();

	const overrideMilliseconds = staleTimeOverrideMinutes
		? staleTimeOverrideMinutes * 1000 * 60
		: null;

	const result: UseQueryResult<T[], unknown> = useQuery({
		queryKey: [key],
		queryFn: () => fetcher(param),
		staleTime: overrideMilliseconds ?? Number.POSITIVE_INFINITY, // never refetch automatically
		gcTime: overrideMilliseconds ?? Number.POSITIVE_INFINITY, // never garbage collect
	});

	const clearCache = () => queryClient.removeQueries({ queryKey: [key] });

	return {
		values: result.data ?? [],
		isError: isNotNullOrUndefined(result.error),
		isLoading: result.isLoading,
		error: result.error,
		clearCache,
	};
}
